import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import MainContainer from "../../layout/MainContainer";
import SearchBar from "../../components/SearchBar";
import {
  Box,
  IconButton,
  Button,
  Menu,
  MenuItem,
  Tooltip,
} from "@mui/material";
import AddNotification from "../../Modals/addNotification";
import {
  useAddNotificationMutation,
  useGetNotificationListMutation,
  useLazyDeleteNotificationQuery,
} from "../../service/notifications";
import { generateEncryptedKeyBody } from "../../utils/crypto";
import { CommonBody, Notifications } from "../../types/General";
import { errorToast, successToast } from "../../helpers";
import Loader from "../../constants/Loader";
import { GridColDef } from "@mui/x-data-grid";
import TableComponent from "../../components/TableComponent";
import { isValidInput } from "../../utils/validations";
import { ConfirmationModal } from "../../Modals/confirmationModal";
import moment from "moment";

const TARGET_GROUP: Record<number, string> = {
  1: "All",
  2: "Sub Admin",
  3: "Individuals",
  4: "Wizard Team",
  5: "Freelancer Agents",
  6: "B2B",
};

const ManageNotifications = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);

  const [error, setError] = useState(false);
  const [title, setTitle] = useState("");
  const [message, setMessage] = useState("");
  const [checked, setChecked] = useState(false);
  const [selectedOptions, setSelectedOptions] = useState<any[]>([]);

  const [searchTerm, setSearchTerm] = useState<string>("");
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState<string>("");
  const [count, setCount] = useState(0);
  const [rows, setRows] = useState<Notifications[]>([]);
  const [page, setPage] = useState<number>(1);
  const [openWarnModal, setOpenWarnModal] = useState(false);
  const [selectedId, setSelectedId] = useState<number | null>(null);

  const [anchorEl1, setAnchorEl1] = React.useState<null | HTMLElement>(null);
  const open1 = Boolean(anchorEl1);
  const handleClick1 = (
    event: React.MouseEvent<HTMLButtonElement>,
    rowId: number
  ) => {
    setAnchorEl1(event.currentTarget);
    setSelectedId(rowId);
  };
  const handleClose1 = () => {
    setAnchorEl1(null);
    setSelectedId(null);
  };

  const [openModal, setOpenModal] = useState(false);
  const handleCloseModal = () => {
    setOpenModal(false);
  };

  const [addNotification] = useAddNotificationMutation();
  const [getNotifications] = useGetNotificationListMutation();
  const [deleteNotification] = useLazyDeleteNotificationQuery();

  const handlePageChange = (event: any, newPage: number) => {
    setPage(newPage + 1);
  };

  const fetchNotifications = async () => {
    const body = {
      start: page,
      length: 10,
      search: debouncedSearchTerm.trim(),
    };

    setLoading(true);

    try {
      const encryptedBody = generateEncryptedKeyBody(body) as CommonBody;
      const response = await getNotifications(encryptedBody).unwrap();
      setLoading(false);

      if (response?.status === 200) {
        setRows(response?.data?.response_object || []);
        setCount(response?.data?.total_records);
      } else {
        setRows([]);
      }
    } catch (error: any) {
      errorToast(error?.data?.message || "");
      setLoading(false);
    }
  };

  const handleSubmit = async () => {
    if (!title?.length || !message?.length) return;
    if (!checked && !selectedOptions?.length) {
      errorToast("Please select user types to send notification");
      return;
    }

    const selectedValues = selectedOptions?.map((option) => option?.value);
    const body = {
      title: title,
      message: message,
      target_group: checked ? [1] : selectedValues,
      type: 1,
      // 1 for broadcast and 2 for email
    };

    try {
      setLoading(true);
      const encryptedBody = generateEncryptedKeyBody(body) as CommonBody;

      const response = await addNotification(encryptedBody).unwrap();

      if (response?.status === 200) {
        setLoading(false);

        successToast("Notification sent successfully");
        fetchNotifications();
        setTitle("");
        setMessage("");
        setSelectedOptions([]);
        setOpenModal(false);
        setError(false);
        setChecked(false);
      } else {
        setLoading(false);
      }
    } catch (error: any) {
      errorToast(error?.data?.message || "");
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    setLoading(true);
    try {
      const response = await deleteNotification({ id: selectedId }).unwrap();
      setLoading(false);

      if (response?.status === 200) {
        successToast("Notification deleted successfully");
        fetchNotifications();
      }
    } catch (error: any) {
      errorToast(error?.data?.message || "");
      setLoading(false);
    }
  };

  const columns: GridColDef<(typeof rows)[number]>[] = [
    {
      flex: 1,
      minWidth: 200,
      field: "title",
      headerName: "Title",
      editable: false,
      sortable: false,
      renderCell: ({ row }) => {
        return (
          <div className="user_block">
            <p>{row?.title || "-"}</p>
          </div>
        );
      },
    },
    {
      flex: 1,
      minWidth: 200,
      field: "message",
      headerName: "Message",
      editable: false,
      sortable: false,
      renderCell: ({ row }) => {
        const message = row?.message ?? "";
        const truncatedMessage =
          message.length >= 40 ? `${message.slice(0, 40)}...` : message;

        return (
          <div className="user_block">
            <p>
              <Tooltip title={<span>{message}</span>}>
                <span>{truncatedMessage}</span>
              </Tooltip>
            </p>
          </div>
        );
      },
    },
    {
      flex: 1,
      minWidth: 200,
      field: "sentTo",
      headerName: "Sent To",
      editable: false,
      sortable: false,
      renderCell: ({ row }) => {
        const targetGroupNames =
          row?.target_groups
            ?.map((groupId) => TARGET_GROUP[groupId])
            .join(", ") || "-";

        return (
          <div className="user_block">
            <p>{targetGroupNames}</p>
          </div>
        );
      },
    },
    {
      flex: 1,
      minWidth: 200,
      field: "date",
      headerName: "Date",
      editable: false,
      sortable: false,
      renderCell: ({ row }) => {
        return (
          <div className="user_block">
            <p>{moment(row?.created_at).format("DD MMM YYYY") || "-"}</p>
          </div>
        );
      },
    },

    {
      flex: 0.5,
      minWidth: 140,
      field: "action",
      headerName: "action",
      editable: false,
      sortable: false,
      renderCell: ({ row }) => {
        return (
          <div className="table_actions v2">
            <Box>
              <IconButton
                aria-controls={open1 ? "basic-menu1" : undefined}
                aria-haspopup="true"
                aria-expanded={open1 ? "true" : undefined}
                onClick={(event) => handleClick1(event, row?.id)}
              >
                <img src="/static/images/more_icon.svg" alt="" />
              </IconButton>
              <Menu
                className="setting_menu"
                id="basic-menu1"
                anchorEl={anchorEl1}
                open={open1}
                onClose={handleClose1}
                MenuListProps={{
                  "aria-labelledby": "basic-button1",
                }}
                PaperProps={{
                  elevation: 0,
                  sx: {
                    overflow: "visible",
                    boxShadow: "0px 4px 20px 0px rgba(0, 0, 0, 0.05)",
                    borderRadius: "8px",
                    mt: 1,
                  },
                }}
                transformOrigin={{ horizontal: "right", vertical: "top" }}
                anchorOrigin={{ horizontal: "right", vertical: "bottom" }}
              >
                <MenuItem
                  onClick={() => {
                    setAnchorEl1(null);
                    setOpenWarnModal(true);
                  }}
                >
                  Delete
                </MenuItem>
              </Menu>
            </Box>
          </div>
        );
      },
    },
  ];

  useEffect(() => {
    fetchNotifications();
  }, [debouncedSearchTerm, page]);

  return (
    <MainContainer>
      <Loader isLoad={loading} />
      <div className="main_title">
        <h1>Manage Notifications</h1>
        <p>
          <span onClick={() => navigate("/dashboard")}>Dashboard</span> - Manage
          Notifications
        </p>
      </div>

      <div className="cards">
        <div className="table_header">
          <div className="left_s">
            <SearchBar
              searchTerm={searchTerm}
              placeholder="Search"
              setDebouncedSearchTerm={setDebouncedSearchTerm}
              value={searchTerm}
              onCross={() => setSearchTerm("")}
              onChange={(val: any) => {
                if (isValidInput(val.target.value)) {
                  setSearchTerm(val.target.value);
                }
              }}
            />
          </div>
          <div className="right_s">
            <Button
              className="btn btn_secondary"
              onClick={() => setOpenModal(true)}
            >
              Add Notification
            </Button>
          </div>
        </div>
        <div className="sorting_table">
          <TableComponent
            columns={columns}
            rows={rows}
            count={count}
            page={page}
            onPageChange={handlePageChange}
            loading={loading}
          />
        </div>
      </div>

      <AddNotification
        open={openModal}
        onClose={handleCloseModal}
        setOpen={setOpenModal}
        handleSubmit={handleSubmit}
        title={title}
        setTitle={setTitle}
        message={message}
        setMessage={setMessage}
        error={error}
        setError={setError}
        setSelectedOptions={setSelectedOptions}
        checked={checked}
        setChecked={setChecked}
        selectedOptions={selectedOptions}
      />
      <ConfirmationModal
        text={"Are you sure to delete this notification?"}
        openModal={openWarnModal}
        setOpenModal={setOpenWarnModal}
        handleSubmit={handleDelete}
      />
    </MainContainer>
  );
};

export default ManageNotifications;
